import { useQuery } from '@tanstack/vue-query'
import { fetchBootstrap } from '~/api/bootstrap'
import {
  deviceZone,
  formatHm,
  fromLocal,
  isValidZone,
  localDate,
  offsetLabel,
  offsetSeconds,
  serverNow,
} from '~/utils/time'

/**
 * 站点时间上下文（规范 6.2）。
 *
 * - 站点时区**只**来自 GET /bootstrap 的 site.timezone（公开接口，整个会话取一次）；
 * - 「今日 / 近 7 日 / 本月」「每日 00:00 重置」这类规则按站点日界生效，设备时区与站点时区
 *   不同时才提示「按站点时间（UTC-3）统计」、在规则里补「即你当地 23:00」；
 * - 只在客户端挂载后给出判断（bootstrap 查询不在服务端跑），服务端与水合阶段恒为「不提示」。
 */
export function useSiteContext() {
  const { t } = useI18n()

  const { data } = useQuery({
    queryKey: ['site', 'bootstrap'],
    queryFn: fetchBootstrap,
    // 租户时区创建后不可改：一次会话取一次即可
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    retry: 2,
  })

  const mounted = ref(false)
  onMounted(() => {
    mounted.value = true
  })

  const { now } = useServerClock(60_000)

  /** 站点 IANA 时区；未取到或非法时为 null（不回落 UTC） */
  const siteZone = computed<string | null>(() => {
    const tz = data.value?.site?.timezone
    return isValidZone(tz) ? tz : null
  })

  /** 设备时区（挂载后才有；取不到 IANA 名时为 null，按运行环境默认时区处理） */
  const localZone = computed<string | null>(() => (mounted.value ? deviceZone() : null))

  const siteOffset = computed<number | null>(() =>
    siteZone.value ? offsetSeconds(now.value, siteZone.value) : null,
  )

  /** 站点偏移标签，如 UTC-3；站点时区未知时为空串 */
  const siteOffsetLabel = computed(() =>
    siteOffset.value === null ? '' : offsetLabel(siteOffset.value),
  )

  /** 设备与站点此刻的偏移不同（日界不同）才为 true */
  const zonesDiffer = computed(() => {
    if (!mounted.value || siteOffset.value === null) return false
    return offsetSeconds(now.value, localZone.value ?? undefined) !== siteOffset.value
  })

  /**
   * 计算「站点日期」用的时区：站点时区优先；未取到时退回设备时区（只影响界面默认值，
   * 真正的统计窗口由服务端按站点时区切，接口不接受前端算的「今天」）。
   */
  const calendarZone = computed<string | undefined>(
    () => siteZone.value ?? localZone.value ?? undefined,
  )

  /** 站点「今天」（YYYY-MM-DD） */
  const siteToday = () => localDate(serverNow(), calendarZone.value)

  /**
   * 规则文案括注：「（站点时间 UTC-3）」；设备时区不同时补后半句
   * 「（站点时间 UTC-3，即你当地 23:00）」。站点时区未知时返回空串（不写括注）。
   * hour/minute 是站点当地钟点（如每日 00:00 重置）；withLocal=false 只写站点时间
   * （每周一 / 每月 1 号这类换到当地可能跨日的规则，不折算当地钟点）。
   */
  const siteClockNote = (hour = 0, minute = 0, withLocal = true): string => {
    const tz = siteZone.value
    if (!tz || siteOffsetLabel.value === '') return ''
    if (!withLocal || !zonesDiffer.value) {
      return t('common.time.siteClockNote', { offset: siteOffsetLabel.value })
    }
    const today = localDate(now.value, tz)
    const at = fromLocal(
      {
        year: Number(today.slice(0, 4)),
        month: Number(today.slice(5, 7)),
        day: Number(today.slice(8, 10)),
        hour,
        minute,
      },
      tz,
    )
    return t('common.time.siteClockNoteLocal', {
      offset: siteOffsetLabel.value,
      local: formatHm(at, localZone.value ?? undefined),
    })
  }

  /** 「站点时间 UTC-3」；站点时区未知时为「站点时间」 */
  const siteTimeLabel = computed(() =>
    siteOffsetLabel.value
      ? t('common.time.siteTimeWithOffset', { offset: siteOffsetLabel.value })
      : t('common.time.siteTime'),
  )

  return {
    siteZone,
    localZone,
    siteOffsetLabel,
    zonesDiffer,
    calendarZone,
    siteToday,
    siteClockNote,
    siteTimeLabel,
  }
}
